import {
  JupiterTheme,
  useFeatureFlags,
  useABAC,
} from '@jupiterone/jupiter-web-toolkit';
import Key from '@jupiterone/react-brand-icons/jupiterone/class/Key';
import { Grid, Typography, Zoom } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { useEffect, useState } from 'react';
import { Lock, Mail, Sliders, User, Users, Bell } from 'react-feather';
import SpeedDialIcon from 'react-feather/dist/icons/settings';
import { AccountModalPage } from 'navbar/src/jupiterAccountSettings/types';
import IconAvatarNav from './IconAvatarNav';
import { FeatureEntitlementName } from '@jupiterone/entitlement-types';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    gridHeaderItem: {
      background: `linear-gradient(180deg,
  rgba(249, 249, 249, 0.35) 0%,
  rgba(249, 249, 249, 0.15) 100%)`,
      borderRadius: '4px 0 0 4px',
      overflow: 'hidden',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexFlow: 'column',
      '&:before': {
        background: theme.palette.primary.dark,
        borderRadius: '4px 0 0 0',
        display: 'block',
        content: `''`,
        position: 'absolute',
        height: 8,
        top: 0,
        left: 0,
        width: '100%',
        zIndex: 1,
      },
    },
    groupPanelHeader: {
      alignItems: 'center',
      marginBottom: 24,
      display: 'flex',
      flexFlow: 'column',
      justifyContent: 'center',
      minHeight: 204,
      paddingTop: 22,
      zIndex: 2,
      alignSelf: 'flex-start',
      width: '100%',
      '& svg': {
        color: theme.palette.primary.main,
        height: 56,
        strokeWidth: 1,
        width: 56,
      },
    },
    groupPanelHeaderText: {
      lineHeight: 0.9,
      color: theme.palette.primary.main,
      textAlign: 'center',
    },
    headerIcon: {
      borderRadius: '50%',
      border: 'solid 2px #ffffff',
      background: `linear-gradient(315deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.75) 100%)`,
      boxShadow: '0 0 25px 0 rgb(0, 6, 42, 0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 20,
      marginTop: 20,
      padding: 24,
    },
    backgroundIcon: {
      position: 'absolute',
      color: theme.palette.common.white,
      width: 550,
      height: 550,
      zIndex: 0,
      bottom: -180,
      left: -285,
      strokeWidth: 0.075,
      strokeOpacity: 0.3,
    },
  })
);

export interface NavPage {
  key: AccountModalPage;
  name: string;
  icon: JSX.Element;
  isEnabled: boolean;
  entitlement?: FeatureEntitlementName;
}

interface Props {
  activePage: AccountModalPage;
  onPageChange: (newPage: AccountModalPage) => void;
}

const LeftNav = ({ activePage, onPageChange }: Props) => {
  const { isAdmin, enforce } = useABAC();
  const classes = useStyles();
  const { hasFeature } = useFeatureFlags();

  const pages: NavPage[] = [
    {
      key: 'invitations',
      name: 'Invitations',
      icon: <Mail />,
      isEnabled: true,
    },
    {
      key: 'profile',
      name: 'My Profile',
      icon: <User />,
      isEnabled: true,
    },
    {
      key: 'notifications',
      name: 'Notifications',
      icon: <Bell />,
      isEnabled: hasFeature('notifications'),
    },
    {
      key: 'access',
      name: 'Users & Access',
      icon: <Users />,
      isEnabled: isAdmin,
    },
    {
      key: 'accountManagement',
      name: 'Account Management',
      icon: <Sliders />,
      isEnabled: isAdmin,
    },
    {
      key: 'apiKeys',
      name: 'API Keys',
      icon: <Key />,
      isEnabled: enforce(['apiKeyUser']),
    },
    {
      key: 'clients',
      name: 'Single Sign On',
      icon: <Lock />,
      entitlement: FeatureEntitlementName.SAML_SSO,
      isEnabled: true,
    },
  ];

  const activeNavPage: NavPage | undefined = pages.find(
    (p: NavPage) => p.key === activePage
  );
  const enabledNavPages: NavPage[] = pages.filter((page) => page.isEnabled);

  const [zoom, setZoom] = useState<boolean>(true);

  const zoomTransition = () => {
    setZoom(false);
    setTimeout(() => {
      setZoom(true);
    }, 200);
  };

  useEffect(() => {
    zoomTransition();
  }, []);

  const handlePageChange = (newPage: AccountModalPage) => {
    onPageChange(newPage);
    zoomTransition();
  };

  return (
    <Grid item={true} sm={4} md={4} className={classes.gridHeaderItem}>
      <Zoom in={zoom} timeout={360}>
        <div className={classes.groupPanelHeader}>
          <div className={classes.headerIcon} style={{ opacity: zoom ? 1 : 0 }}>
            {activeNavPage && activeNavPage.icon}
          </div>
          <Typography
            variant="subtitle1"
            color="primary"
            className={classes.groupPanelHeaderText}
            style={{ opacity: zoom ? 1 : 0 }}
          >
            {activeNavPage && activeNavPage.name}
          </Typography>
        </div>
      </Zoom>
      <IconAvatarNav
        onPageChange={handlePageChange}
        activePage={activePage}
        navPages={enabledNavPages}
      />
      <SpeedDialIcon className={classes.backgroundIcon} />
    </Grid>
  );
};

export default LeftNav;
